import { isLocale } from '@upweo/i18n';
import { useI18n } from '@upweo/ui';
import { useEffect, useRef } from 'react';

import * as api from './api.js';
import { useAuth } from './auth-context.js';

/**
 * Keep the signed-in user's saved language in step with the picker.
 *
 * The UI language lives in localStorage, which is right for the interface: it
 * applies instantly and works before anyone has signed in. But localStorage is
 * invisible to the API, and a password-reset email is composed with no browser
 * in the conversation at all — so the choice has to reach the server too.
 *
 * Two directions, and the order matters:
 *
 *   1. On sign-in, ADOPT the saved language, but only if this browser has no
 *      choice of its own. Someone who just switched to German on this device
 *      should not be yanked back by a preference they set months ago elsewhere.
 *   2. Afterwards, PUSH every change up.
 *
 * A render-nothing component rather than a hook inside the provider, because
 * the provider lives in @upweo/ui and must not know about this app's API.
 */
export const LocaleSync = (): null => {
  const { user } = useAuth();
  const { locale, setLocale } = useI18n();
  /** Guards the adopt step so it runs once, before any push. */
  const adopted = useRef(false);
  /** What the server already knows, so we do not PATCH it back to itself. */
  const lastPushed = useRef<string | null>(null);

  useEffect(() => {
    if (!user || adopted.current) {
      return;
    }
    adopted.current = true;
    lastPushed.current = user.locale;

    const storedHere = window.localStorage.getItem('upweo_admin_locale');
    if (storedHere === null && user.locale !== null && isLocale(user.locale)) {
      setLocale(user.locale);
    }
  }, [user, setLocale]);

  useEffect(() => {
    if (!user || !adopted.current || lastPushed.current === locale) {
      return;
    }
    lastPushed.current = locale;
    // Fire and forget: failing to save a language preference must never
    // interrupt whatever the user was actually doing.
    void api.updateMyLocale(locale).catch(() => undefined);
  }, [locale, user]);

  return null;
};
